import { lazy, Suspense, Component } from "react";
import { BrowserRouter, Routes, Route, Link } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { ViewProvider, useView } from "./context/ViewContext";
import CaseStudyPage from "./components/CaseStudyPage";
import OnePager from "./components/OnePager";
import PatternHunt from "./components/PatternHunt";
import QmsSection from "./components/QmsSection";
import Nav from "./components/Nav";
import Footer from "./components/Footer";
import Hero from "./components/Hero";
import Impact from "./components/Impact";
import Pillars from "./components/Pillars";
import ProjectExplorer from "./components/ProjectExplorer";
import RcaLab from "./components/RcaLab";
import ContinuousImprovement from "./components/ContinuousImprovement";
import Automation from "./components/Automation";
import QualitySystems from "./components/QualitySystems";
import Expertise from "./components/Expertise";
import About from "./components/About";
import Contact from "./components/Contact";
import { SectionHeader, FadeIn } from "./components/common";
import { TrendingUp } from "lucide-react";

const AnalyticsLab = lazy(() => import("./components/AnalyticsLab"));
const IotSection = lazy(() => import("./components/IotSection"));

const LazyFallback = (
  <div className="max-w-7xl mx-auto px-4 sm:px-6 py-20">
    <div className="panel p-8 animate-pulse-soft">
      <div className="h-4 w-48 bg-teal/20 rounded-sm" />
      <div className="h-3 w-96 max-w-full bg-teal/10 rounded-sm mt-4" />
    </div>
  </div>
);

const TARGET_ROLES = [
  "Automation & Digital Projects Specialist",
  "Digital Manufacturing Specialist",
  "MSAT Digital Specialist",
  "Manufacturing Systems Specialist",
  "Manufacturing Data Analyst",
  "Process Automation Specialist",
  "Operational Excellence Specialist",
  "Continuous Improvement Engineer",
  "Quality Systems Specialist",
  "Digital Quality Specialist",
  "Manufacturing Analytics Specialist",
  "Technical Business Analyst — Manufacturing",
  "Digital Transformation Specialist",
];

const INDUSTRIES = ["Pharmaceutical Manufacturing", "Biotechnology", "Analytical / Product Quality Laboratories", "Diagnostics", "Advanced Therapies"];
const TOOLS = ["Python", "pandas", "REST APIs", "JMP", "Retool", "IoT / Sensor APIs", "Data Pipelines", "Windows Task Scheduler"];

function RecruiterSummary() {
  const CAP_CARDS = [
    { t: "Product Quality", d: "RCA, CAPA, process risk and manufacturing support.", testId: "recruiter-cap-product-quality" },
    { t: "Automation", d: "Python, APIs, data pipelines and digital workflows.", testId: "recruiter-cap-automation" },
    { t: "QMS", d: "SOP, WI, deviation, CAPA, ECO/change and controlled-document lifecycle.", testId: "recruiter-cap-qms" },
    { t: "Analytics", d: "FPY, Pareto, lot, instrument and statistical analysis.", testId: "recruiter-cap-analytics" },
    { t: "IoT", d: "Environmental data acquisition and time-series reliability.", testId: "recruiter-cap-iot" },
    { t: "Continuous Improvement", d: "Evidence → improvement → effectiveness monitoring.", testId: "recruiter-ci-summary" },
  ];
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-20" data-testid="recruiter-summary">
      <SectionHeader overline="60-Second Summary" title="Manufacturing Automation & Product Quality Systems" />
      <p className="text-pale/85 text-base leading-relaxed max-w-3xl mt-6">
        Streamlining Manufacturing Data, Product Quality Systems &amp; Digital Workflows — combining regulated Product Quality, manufacturing
        investigations, QMS documentation, automation, IoT and manufacturing analytics to solve recurring operational problems and implement
        controlled improvements.
      </p>
      <div className="flex flex-wrap gap-1.5 mt-5" data-testid="recruiter-quick-strip">
        {[
          "9+ Years Regulated Laboratory / Manufacturing Experience",
          "Product Quality Systems",
          "cGMP / GxP",
          "RCA / CAPA",
          "QMS / Controlled Documentation",
          "Python / API Automation",
          "Manufacturing Analytics",
          "Data Integrity / ALCOA+",
          "Validation / Qualification",
          "Operational Excellence",
          "Environmental Monitoring / IoT",
        ].map((s) => (
          <span key={s} className="tech-tag">{s}</span>
        ))}
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-8" data-testid="recruiter-capabilities">
        {CAP_CARDS.map((c, i) => (
          <FadeIn key={c.t} delay={i * 0.05}>
            <div className="panel p-5 h-full" data-testid={c.testId}>
              <p className="overline-label">{c.t}</p>
              <p className="text-xs text-pale/80 mt-3 leading-relaxed">{c.d}</p>
            </div>
          </FadeIn>
        ))}
      </div>
      <div className="grid md:grid-cols-2 gap-4 mt-4">
        <FadeIn>
          <div className="panel p-5 h-full border-l-2 border-l-success" data-testid="recruiter-impact-highlight">
            <TrendingUp className="w-5 h-5 text-success" aria-hidden="true" />
            <p className="font-mono text-2xl text-success mt-3">~45%</p>
            <p className="font-sans text-sm font-semibold text-paper mt-1">Scrap Reduction</p>
            <p className="text-xs text-steel mt-2">CAPA/RCA-driven process improvement (professional impact).</p>
          </div>
        </FadeIn>
        <FadeIn delay={0.06}>
          <div className="panel p-5 h-full" data-testid="recruiter-tools">
            <p className="overline-label">Tools</p>
            <div className="flex flex-wrap gap-1.5 mt-3">
              {TOOLS.map((t) => (
                <span key={t} className="font-mono text-[10px] px-2 py-1 rounded-sm bg-navy border border-teal/30 text-pale/80">{t}</span>
              ))}
            </div>
            <p className="overline-label mt-4">Industries</p>
            <div className="flex flex-wrap gap-1.5 mt-2">
              {INDUSTRIES.map((i) => (
                <span key={i} className="font-mono text-[10px] px-2 py-1 rounded-sm bg-navy border border-teal/30 text-pale/80">{i}</span>
              ))}
            </div>
          </div>
        </FadeIn>
      </div>
      <div className="panel p-5 mt-4" data-testid="recruiter-target-roles">
        <p className="overline-label">Suited Roles</p>
        <div className="flex flex-wrap gap-1.5 mt-3">
          {TARGET_ROLES.map((r) => (
            <span key={r} className="font-mono text-[10px] px-2 py-1 rounded-sm bg-navy border border-teal/30 text-pale/80">{r}</span>
          ))}
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4 mt-4">
        <Link to="/recruiter-one-pager" data-testid="recruiter-onepager-link" className="panel p-5 hover:border-cyan/50 transition-colors block">
          <p className="overline-label">Recruiter One-Pager</p>
          <p className="text-xs text-steel mt-2">A print-friendly single-page summary — save as PDF straight from the browser.</p>
        </Link>
        <Link to="/analytics-lab/pattern-hunt" data-testid="recruiter-pattern-hunt-link" className="panel p-5 hover:border-cyan/50 transition-colors block">
          <p className="overline-label">Live Pattern Hunt</p>
          <p className="text-xs text-steel mt-2">A 3-minute guided challenge on the deterministic synthetic dataset: find the hidden INS-04 and RGT-107 signals.</p>
        </Link>
      </div>
    </section>
  );
}

function TechnicalHome() {
  return (
    <>
      <Hero />
      <Impact />
      <Pillars />
      <ProjectExplorer />
      <RcaLab />
      <ContinuousImprovement />
      <QmsSection />
      <Automation />
      <Suspense fallback={LazyFallback}>
        <IotSection />
      </Suspense>
      <Suspense fallback={LazyFallback}>
        <AnalyticsLab />
      </Suspense>
      <QualitySystems />
      <Expertise />
      <About />
      <Contact />
    </>
  );
}

function RecruiterHome() {
  return (
    <>
      <Hero />
      <Impact />
      <RecruiterSummary />
      <ProjectExplorer recruiterLimit={4} />
      <Contact />
    </>
  );
}

function Home() {
  const { isRecruiter } = useView();
  return isRecruiter ? <RecruiterHome /> : <TechnicalHome />;
}

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  componentDidCatch(error, info) {
    console.error("App error boundary caught:", error, info);
  }
  render() {
    if (this.state.error) {
      return (
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-24 text-center" data-testid="error-boundary">
          <p className="overline-label">Something went wrong</p>
          <h1 className="font-sans tracking-tight text-3xl text-paper mt-4">This view hit an unexpected error</h1>
          <p className="text-steel text-sm mt-4">The rest of the portfolio is unaffected.</p>
          <button
            data-testid="error-boundary-reset"
            onClick={() => this.setState({ error: null })}
            className="mt-6 bg-cyan text-navy font-sans font-semibold text-sm px-5 py-2.5 rounded-sm hover:bg-pale transition-colors"
          >
            Try again
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

function App() {
  return (
    <div className="min-h-screen bg-navy text-paper font-body">
      <ViewProvider>
        <BrowserRouter>
          <Nav />
          <main>
            <ErrorBoundary>
              <Routes>
                <Route path="/projects/:slug" element={<CaseStudyPage />} />
                <Route path="/recruiter-one-pager" element={<OnePager />} />
                <Route path="/analytics-lab/pattern-hunt" element={<PatternHunt />} />
                <Route path="*" element={<Home />} />
              </Routes>
            </ErrorBoundary>
          </main>
          <Footer />
        </BrowserRouter>
        <Toaster theme="dark" position="bottom-right" toastOptions={{ style: { background: "#0D1B2A", border: "1px solid rgba(32,184,205,0.3)", color: "#F7F9FB" } }} />
      </ViewProvider>
    </div>
  );
}

export default App;
